// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Runtime companion to `SessionEventType` — the single source of truth for
 * every consumer that needs the values at run time (backend validation,
 * frontend filter options, i18n label parity tests).
 *
 * Before Sprint 63.0.1 three separate lists existed: this type (5 values), an
 * inline validator array in `session-event-service.ts`, and a hardcoded
 * 10-value array in `SessionDashboardPage.tsx`. They had drifted far enough
 * that the dashboard's event-type filter could not match any row it rendered.
 * Import this — do not re-declare the values.
 */
export const SESSION_EVENT_TYPES = [
    'session_started',
    'session_refreshed',
    'session_expired',
    'session_terminated',
    'session_revoked',
];
/**
 * Sprint 63.29.0 (Q5, #540) — `advisor_summary` is a person asking for Forseti's
 * one-paragraph summary of ONE record (`GET …/:id/advisor-summary`), from the
 * *Show summary* control or when they start a Forseti chat about the record.
 *
 * It replaces the module-audit event `ai_advisor_record_summary_fetched`, which
 * every summary endpoint used to write into the record's own audit trail: a
 * read recorded among a record's acts, so an evidence pack assembled after
 * someone merely opened an edit dialog showed drift. A read is an access-log
 * row. It is its own value rather than `single` because "who opened the record"
 * and "who asked Forseti to summarise it" are different questions in an access
 * review. Written only by `middleware/access-logging.ts`; never client-submittable.
 */
export const ADVISOR_SUMMARY_ACCESS_TYPE = 'advisor_summary';
/**
 * The subset of `AccessType` a client may submit to
 * `POST /access-accountability/access-log`.
 *
 * `advisor_search` is deliberately absent. That value asserts *the platform's
 * own runtime performed this read*, and a value only the platform writes is
 * only trustworthy while only the platform can write it — otherwise any holder
 * of `access_accountability:write` could manufacture advisor attributions, or
 * launder a direct read into one. `AccessLogService.create` validates against
 * this constant rather than a hand-typed literal so widening `AccessType` again
 * cannot silently widen the API surface with it.
 */
export const CLIENT_SUBMITTABLE_ACCESS_TYPES = [
    'single',
    'list',
];
/**
 * The transport a record read was performed over.
 *
 * `access_type` already answers *what shape of read* happened; this answers
 * *which door it came through*, and the two are independent. A `list` and a
 * `single` both arrive over `web`; an `advisor_search` arrives over `chat` or
 * over `mcp`, and those two are the pair an access review has to be able to
 * separate — see the note on `AccessLogEntry.access_surface`.
 *
 * `web` is the HTTP request path a signed-in browser session takes, recorded by
 * `middleware/access-logging.ts`. It is the only value that writer produces, and
 * it passes it explicitly rather than letting the field default: a default is a
 * value nobody chose, and this field's whole purpose is to say who chose it.
 *
 * Declared as a value array with the union derived from it, for validators,
 * filters and label parity tests. Declared here rather than re-typed at each
 * consumer for the same reason `SESSION_EVENT_TYPES` is.
 *
 * It used to be the other way round — a hand-written union with a separate
 * `as const satisfies readonly AccessSurface[]` array beside it. `satisfies`
 * proves each member is a valid surface; it does not prove the array is
 * complete, so the constant did not pin the union it claimed to companion, and
 * a fourth transport could be added to the union while every runtime consumer
 * kept the old three. Deriving one from the other closes that direction too.
 */
export const ACCESS_SURFACES = ['web', 'chat', 'mcp'];
/**
 * Runtime value export — the canonical classification-level list, for code
 * that must validate or offer the members (a zod enum, a select). Import this
 * rather than spelling the five values out: the vocabulary census
 * (`scripts/shared-vocabulary-census.ts`) reports a transcription of the whole
 * member set as a re-derivation.
 */
export const CLASSIFICATION_LEVELS = [
    'public',
    'internal',
    'confidential',
    'restricted',
    'unclassified',
];
const classificationLevelsCoverUnion = true;
void classificationLevelsCoverUnion;
// ── Access-log retention (Sprint 63.1.0) ────────────────────
/**
 * Why a row the sweep looked at was not destroyed — or that it was.
 *
 * Every row a sweep reads lands in exactly one bucket and the counts are
 * reported, so an auditor reading the register never has to infer the
 * difference between *retained* and *not looked at*.
 */
export const ACCESS_LOG_RETENTION_BUCKETS = [
    /** Past its window, unheld, unanchored: `purge_after` stamped, bytes leave. */
    'swept',
    /** An active legal hold or a pack retention block covers it. */
    'retained_held',
    /** A `grc_access_proofs.access_id` names it; destroying it would orphan a chain. */
    'retained_anchored',
    /**
     * It carries no `expires_at`, so it has no declared window and is **never**
     * swept. Pre-63.0.1 rows and any future writer that forgets land here. The
     * bucket names the population rather than guessing at it.
     */
    'unstamped',
    /** Already accounted for by an earlier run — `purge_after` is set. */
    'skipped_swept',
];
export const ACCESS_LOG_RETENTION_TRIGGERS = ['scheduled', 'on_demand'];
/**
 * Runtime value export — the canonical investigation type list.
 *
 * Before this was added, `CreateInvestigationSchema` in the backend declared
 * its own six-value enum (`unauthorized_access` / `privilege_abuse` /
 * `policy_violation` / `anomaly_detected` / `audit_finding` / `other`) that
 * shared **no value at all** with this type. Since the create route parses the
 * request body through that schema, and the frontend modal and all 16 locale
 * files offer only the values above, every New Investigation submission was
 * rejected with a 400 — the modal could not succeed with any type selection.
 * Import this — do not re-declare the values.
 */
export const INVESTIGATION_TYPES = [
    'anomaly_review',
    'complaint_driven',
    'scheduled_review',
    'whistleblower_report',
];
export const INVESTIGATION_SEVERITIES = [
    'low',
    'medium',
    'high',
    'critical',
];
/**
 * Runtime value export — the canonical investigation status list.
 *
 * The backend schema previously allowed `closed` and `escalated`, which are not
 * members of this union, and omitted the three `closed_*` values that
 * `InvestigationService.close()` actually writes.
 */
export const INVESTIGATION_STATUSES = [
    'open',
    'in_progress',
    'pending_review',
    'closed_substantiated',
    'closed_unsubstantiated',
    'closed_inconclusive',
];
export const INVESTIGATION_DISPOSITIONS = [
    'substantiated',
    'unsubstantiated',
    'inconclusive',
];
/**
 * What the proof chain writes. The local SHA-256 chain is complete the moment
 * the proof is inserted: nothing is awaited, so the one state it ever writes is
 * `chained`.
 */
export const ANCHOR_STATUSES_WRITTEN = ['chained'];
/**
 * RESERVED for an external anchor (a proof submitted to a ledger outside this
 * platform). No such anchor exists, so nothing writes these three and a
 * screen must not offer them as a choice until one does. They are kept in the
 * vocabulary, rather than deleted, so the day an external anchor is built its
 * states are already named and the readers already count them. `submitted` was
 * removed: it was declared and never written or counted by anything.
 */
export const ANCHOR_STATUSES_RESERVED_EXTERNAL = ['pending', 'confirmed', 'failed'];
/**
 * Values older rows hold for a proof on the local chain. Every proof written
 * before the vocabulary was narrowed carries `pending` (nothing ever moved it
 * on), and demo data once wrote `anchored`. They are read as `chained` and
 * never rewritten (a proof is append-only).
 */
export const ANCHOR_STATUSES_LEGACY_LOCAL_CHAIN = ['pending', 'anchored'];
/**
 * The status a stored proof is read as. A proof on the local SHA-256 chain whose
 * stored status is absent, `pending` or `anchored` is `chained`; everything else
 * is returned as stored.
 */
export function effectiveAnchorStatus(proof) {
    const stored = proof.anchor_status ?? '';
    const onLocalChain = proof.anchor_type === undefined || proof.anchor_type === 'sha256_chain';
    if (onLocalChain &&
        (stored === '' || ANCHOR_STATUSES_LEGACY_LOCAL_CHAIN.includes(stored))) {
        return 'chained';
    }
    return stored;
}
// ── Sprint 21.8.0 — Access Accountability Maturity ──────────
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const ACCESS_POLICY_REVIEW_FREQUENCIES = [
    'monthly',
    'quarterly',
    'semi_annual',
    'annual',
];
//# sourceMappingURL=access-accountability.js.map